/**
 * The configure screen's unsaved definition. Local to the screen (it is a draft
 * of one form); what the server knows — checks, the saved evaluator — is read
 * from Query and only seeds the first state.
 *
 * Variables come from the prompt (LLM judge) or the picked check (code). A
 * variable the person has not mapped reads its default: the check's own, or a
 * guess from its name for a prompt variable.
 */

import { useState } from "react";
import { formOptions, optionsPayload, toForm, type FormValue } from "./check-options";
import type { MappingRow } from "./mapping-rows";
import type {
  CodeCheck,
  Evaluator,
  EvaluatorCategory,
  EvaluatorCreate,
  EvaluatorKind,
  EvaluatorPatch,
} from "./types";
import { guessPath, promptVariables } from "./variables";

export const STARTER_PROMPT = `Does the answer correctly respond to the request?

Request:
{{input}}

Answer:
{{output}}

Expected answer:
{{expected_output}}`;

export interface Draft {
  kind: EvaluatorKind;
  name: string;
  prompt: string;
  check: string | null;
  options: Record<string, FormValue>;
  /** Only the variables the person (or the template) set. */
  mapping: Record<string, string>;
  threshold: string;
}

export interface DraftSeed {
  kind: EvaluatorKind;
  name?: string;
  prompt?: string | null;
  check?: string | null;
  options?: Record<string, unknown> | null;
  mapping?: Record<string, string | null>;
  pass_threshold?: number;
  /** A template's category and description go with a new evaluator. */
  category?: EvaluatorCategory;
  description?: string;
}

const defined = (m: Record<string, string | null> = {}) =>
  Object.fromEntries(Object.entries(m).filter((e): e is [string, string] => !!e[1]));

function seedDraft(seed: DraftSeed, checks: CodeCheck[]): Draft {
  const check = checks.find((c) => c.check === seed.check);
  const options = Object.fromEntries(
    (check?.options ?? [])
      .filter((o) => seed.options && o.key in seed.options)
      .map((o) => [o.key, toForm(o, seed.options![o.key])]),
  );
  return {
    kind: seed.kind,
    name: seed.name ?? "",
    prompt: seed.prompt ?? (seed.kind === "llm_judge" ? STARTER_PROMPT : ""),
    check: seed.check ?? null,
    options,
    mapping: defined(seed.mapping),
    threshold: String(seed.pass_threshold ?? (seed.kind === "code" ? 1 : 0.7)),
  };
}

export function useEvaluatorDraft(seed: DraftSeed, checks: CodeCheck[], saved?: Evaluator) {
  const [draft, setDraft] = useState(() => seedDraft(seed, checks));
  const check = checks.find((c) => c.check === draft.check);
  const rows: MappingRow[] =
    draft.kind === "llm_judge"
      ? promptVariables(draft.prompt).map((name) => ({ name, required: true }))
      : (check?.variables ?? []).map((v) => ({ name: v.name, label: v.label, required: v.required }));
  const fallback = (name: string) =>
    draft.kind === "llm_judge"
      ? guessPath(name)
      : (check?.variables.find((v) => v.name === name)?.default ?? "");
  const mapping = Object.fromEntries(rows.map((r) => [r.name, draft.mapping[r.name] ?? fallback(r.name)]));
  const sentMapping = Object.fromEntries(
    Object.entries(mapping)
      .map(([k, v]) => [k, v.trim()] as const)
      .filter(([, v]) => v),
  );
  const threshold = Number(draft.threshold);

  const origin = {
    ...(seed.category ? { category: seed.category } : {}),
    ...(seed.description ? { description: seed.description } : {}),
  };
  const body: EvaluatorCreate =
    draft.kind === "llm_judge"
      ? {
          ...origin,
          kind: "llm_judge",
          name: draft.name.trim(),
          prompt: draft.prompt.trim(),
          mapping: sentMapping,
          pass_threshold: threshold,
        }
      : {
          ...origin,
          kind: "code",
          name: draft.name.trim(),
          check: draft.check ?? "",
          options: optionsPayload(check?.options ?? [], draft.options),
          mapping: sentMapping,
          pass_threshold: threshold,
        };

  const definitionProblem =
    draft.kind === "code" && !check
      ? "Pick a check."
      : rows.length === 0
        ? "Add at least one {{variable}} to the prompt."
        : rows.some((r) => r.required && !mapping[r.name]?.trim())
          ? "Map every variable to a field."
          : Number.isNaN(threshold) || threshold < 0 || threshold > 1 || !draft.threshold.trim()
            ? "Pass score must be between 0 and 1."
            : null;
  const problem = definitionProblem ?? (draft.name.trim() ? null : "Give it a name.");

  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  return {
    draft,
    check,
    rows,
    mapping,
    body,
    problem,
    /** Testing needs a working definition, not a name. */
    testBody: { ...body, name: body.name || "Untitled" } as EvaluatorCreate,
    testProblem: definitionProblem,
    patch: saved ? patchFor(saved, body) : null,
    setName: (name: string) => set({ name }),
    setPrompt: (prompt: string) => set({ prompt }),
    /** A new check starts from its own defaults. */
    setCheck: (key: string) => set({ check: key, options: {}, mapping: {} }),
    setOption: (key: string, value: FormValue) =>
      setDraft((d) => ({ ...d, options: { ...formOptions(check?.options ?? [], d.options), [key]: value } })),
    setPath: (name: string, path: string) =>
      setDraft((d) => ({ ...d, mapping: { ...d.mapping, [name]: path } })),
    setThreshold: (threshold: string) => set({ threshold }),
  };
}

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** Only what changed against the saved version; empty means nothing to save. */
function patchFor(saved: Evaluator, body: EvaluatorCreate): EvaluatorPatch {
  const out: EvaluatorPatch = {};
  if (body.name !== saved.name) out.name = body.name;
  if (body.kind === "llm_judge" && body.prompt !== (saved.prompt ?? "")) out.prompt = body.prompt;
  if (body.kind === "code") {
    if (body.check !== saved.check) out.check = body.check;
    if (!same(body.options, saved.options ?? {})) out.options = body.options ?? {};
  }
  const changed = Object.entries(body.mapping ?? {}).filter(([k, v]) => saved.default_mapping[k] !== v);
  if (changed.length) out.mapping = Object.fromEntries(changed);
  if (body.pass_threshold !== saved.pass_threshold) out.pass_threshold = body.pass_threshold!;
  return out;
}
